import axios from "axios";
import {
  API_BASE_URL,
  CDN_ASSETS_BASE_URL,
  CDN_ORIGIN,
  CDN_PUBLIC_PATH,
  GA_MEASUREMENT_ID,
  GTM_CONTAINER_ID,
  IS_PRODUCTION,
  PRODUCTION_HOSTS,
  PUBLIC_KEY,
  REALTIME_HUB_URL,
  VIDEO_INDEXER_ACCOUNT_ID,
  buildGaScriptUrl,
  buildGtmScriptUrl,
  buildVideoIndexerCaptionsUrl,
  buildVideoIndexerManifestUrl,
} from "../config/appConfig";
import {
  appAssetUrl,
  cdnAssetUrl,
  publicAssetUrl,
} from "../config/staticAssets";

export * from "../config/imagesLink";

const isProduction = IS_PRODUCTION;

export const PublicKey = PUBLIC_KEY;

const isAnalyticsEnabled = () => {
  if (!isProduction) return false;
  if (typeof window === "undefined") return false;

  const host = String(window.location.hostname || "").toLowerCase();
  const isPreprodHost = host === "https://account.welinkjobs.com" || host.startsWith("preprod.");
  const isProdHost = PRODUCTION_HOSTS.some(
    (prodHost) => host === prodHost || host.endsWith(`.${prodHost}`),
  );

  if (!isProdHost || isPreprodHost) return false;

  // Track on all pages for production builds.
  return true;
};

const GTM_INIT_FLAG = "__WLJ_GTM_INITIALIZED__";
const GA_INIT_FLAG = "__WLJ_GA_INITIALIZED__";

export const productionWork = isProduction;
export const enableAlgoKey = isProduction;
export const accountId = VIDEO_INDEXER_ACCOUNT_ID;

const BASE_URL = API_BASE_URL;
export const RealTime_Hub = REALTIME_HUB_URL;
export {
  appAssetUrl,
  buildVideoIndexerCaptionsUrl,
  buildVideoIndexerManifestUrl,
  cdnAssetUrl,
  CDN_ASSETS_BASE_URL,
  CDN_ORIGIN,
  CDN_PUBLIC_PATH,
  publicAssetUrl,
};

if (isProduction) {
  console.log = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.error = () => {};
  console.debug = () => {};
}

export const loadGTM = () => {
  if (!isAnalyticsEnabled()) return;
  if (window[GTM_INIT_FLAG]) return;
  if (!GTM_CONTAINER_ID) return;

  const gtmScriptUrl = buildGtmScriptUrl(GTM_CONTAINER_ID);

  // If GTM was added via HTML (recommended), just mark initialized.
  const existing = document.querySelector(
    `script[src*="${gtmScriptUrl}"]`,
  );
  if (existing) {
    window.dataLayer = window.dataLayer || [];
    window[GTM_INIT_FLAG] = true;
    return;
  }

  // Fallback: inject GTM script (idempotent-ish, guarded by existing script/flag).
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const firstScript = document.getElementsByTagName("script")[0];
  const script = document.createElement("script");
  script.async = true;
  script.src = gtmScriptUrl;
  firstScript.parentNode.insertBefore(script, firstScript);

  window[GTM_INIT_FLAG] = true;
};

export const loadGA = () => {
  if (!isAnalyticsEnabled()) return;
  if (window[GA_INIT_FLAG]) return;
  if (!GA_MEASUREMENT_ID) return;

  const gaScriptUrl = buildGaScriptUrl(GA_MEASUREMENT_ID);

  // Ensure global dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Ensure global gtag exists (idempotent)
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  // Ensure the gtag.js library script exists (only one per page)
  const existing = document.querySelector(
    `script[src*="${gaScriptUrl}"]`,
  );

  if (!existing) {
    const s = document.createElement("script");
    s.async = true;
    s.src = gaScriptUrl;
    s.setAttribute("data-wlj-gtag", "true");
    document.head.appendChild(s);
  }

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);

  window[GA_INIT_FLAG] = true;
};

export const trackPageView = (path) => {
  if (!isAnalyticsEnabled()) return;
  loadGTM();
  loadGA();
  if (!window.dataLayer) return;

  const page_path = path || window.location.pathname + window.location.search;
  const page_location = window.location.href;
  const page_title = document.title;

  // GTM SPA tracking: push a page_view-style event to the dataLayer.
  window.dataLayer.push({
    event: "page_view",
    page_path,
    page_location,
    page_title,
  });

  // GA4 SPA tracking: update page context for the GA property.
  if (typeof window.gtag === "function" && GA_MEASUREMENT_ID) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path,
      page_location,
      page_title,
    });
  }
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "access-control-allow-origin": "*",
    // Authorization: `Bearer ${token}`,
    Authorization: ``,
    "Content-Type": "application/json",
    "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("token");
    const hasToken = token && token !== "null" && token !== "undefined";
    if (hasToken) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

// Response interceptor to handle 401 errors gracefully for guests
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const guestModeData = localStorage.getItem("guestMode");
    const token = localStorage.getItem("token");
    const hasToken = token && token !== "null" && token !== "undefined";

    console.log("🔌 Axios Response Error:", { status: error.response?.status, token: !!hasToken, guestMode: guestModeData });

    // If guest user gets 401, return safe empty response instead of error
    // if (error.response?.status === 401 && !hasToken && guestModeData === "true") {
    //   console.log("✅ Guest 401 - returning safe empty response");
    //   return Promise.resolve({
    //     data: {
    //       responseData: {
    //         companiesList: [],
    //         peopleList: [],
    //         paging: { totalPages: 0, totalRecords: 0 },
    //       },
    //     },
    //   });
    // }

    // If authenticated user gets 401, clear storage and redirect to SignIn
    if (error.response?.status === 401 && hasToken) {
      console.log("❌ Auth 401 - redirecting to SignIn");
      localStorage.clear();
        // Dispatch global event
       window.dispatchEvent(new Event("sessionExpired"));
      // window.location.href = "/SignIn";
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
